import { reportPapEvent } from "@mirage/service-product-logging";
import { getDashActionSurface } from "@mirage/shared/hooks/useDashActionSurface";
import { getRoutePathname } from "@mirage/shared/util/route-pathname";
import { dashAppDwellManager } from "./sessionManagers/sessionManagers";

import type {
  PAPEvent,
  PAPEventDefaultProperties,
} from "@mirage/analytics/events/base/event";
import type {
  SessionAwareEventOptions,
  SessionAwareness,
  WithActionSurfaceFn,
} from "@mirage/analytics/session/session-aware-events";
import { buildSessionAwareness } from "@mirage/analytics/session/session-aware-events";
import type {
  SessionManager,
  SessionManagerName,
} from "@mirage/analytics/session/session-manager";
import { tagged } from "@mirage/service-logging";

const logger = tagged("webapp-analytics-logging");

export function reportEvent(
  event: PAPEvent,
  flush = false,
  defaultProperties: PAPEventDefaultProperties = {},
) {
  // Double-logging both dashActionSurface and actionSurface to avoid skewing metrics.
  // We should move to just using dashActionSurface
  const pathname = getRoutePathname();
  const dashActionSurface = getDashActionSurface(pathname);
  if (dashActionSurface) {
    defaultProperties.dashActionSurface =
      event.properties?.dashActionSurface ?? dashActionSurface;
    defaultProperties.actionSurface =
      event.properties?.actionSurface ?? dashActionSurface;
  }

  const dashSessionId = dashAppDwellManager.getSessionIdOrUndefined();
  if (dashSessionId) {
    defaultProperties.dashSessionId = dashSessionId;
  }

  const combinedProperties = {
    // Preserve actual event trigger time if not already given.
    startTimeMs: Date.now(),
    ...defaultProperties,
    ...event.properties,
  };

  reportPapEvent({ ...event, properties: combinedProperties }, flush);
}

export const reportSessionAwareEvent = async (
  sessionManagerNames: SessionManagerName[],
  sessionManagerMap: Record<SessionManagerName, SessionManager | undefined>,
  getEventCallback: (
    sessionAwareness: SessionAwareness,
  ) => PAPEvent | undefined,
  options: SessionAwareEventOptions = {},
) => {
  const sessionAwareness = await buildSessionAwareness(
    sessionManagerNames,
    sessionManagerMap,
    options,
  );

  const event = getEventCallback(sessionAwareness);

  if (!event) {
    logger.error(
      "No event returned from eventCallback for reportSessionAwareEvent",
    );
    return;
  }

  reportEvent(event, options.flush, options.defaultProperties);
};

export const withActionSurface: WithActionSurfaceFn = (callback) => {
  const pathname = getRoutePathname();
  const dashActionSurface = getDashActionSurface(pathname);
  return callback({
    dashActionSurface,
    actionSurface: dashActionSurface,
  });
};
